import {
  getRandomInt,
  getRandomItemFromArray,
} from 'src/replicant/utils/random';
import {
  TeamEventStatus,
  getCurrentTeamEventEndDate,
  getPreviousTeamEventId,
  getTeamEventScore,
} from '../team.getters';
import { TeamState } from '../team.schema';
import { teamChestRewards, teamChestTargetScore } from './teamChest.ruleset';
import { Match2Reward } from 'src/replicant/match2/rewards/match2-rewards.ruleset';
import { createPrng } from '@play-co/replicant';
import { clamp } from 'src/replicant/utils';

/**
 * Get current team chest event status, to determine if the user can claim rewards.
 */
export function getTeamChestEventStatus(args: {
  team: TeamState;
  userId: string;
  eventId: string;
  now: number;
  ignoreAlreadyClaimed?: boolean;
}): TeamEventStatus {
  const score = getTeamEventScore(args.team, args.eventId);
  const targetScore = getTeamChestEventTargetScore(
    args.team,
    args.eventId,
    args.now,
  );
  const member = args.team.global.members[args.userId];
  const contribution = member.contributions[args.eventId];

  // Check if team has enough score to claim the rewards.
  if (score < targetScore) {
    return 'in-progress';
  }

  // Check if user has contributed to the event.
  if (!contribution || !contribution.score) {
    return 'complete-no-contribution';
  }

  // Check if user has already claimed the rewards.
  if (contribution.claimedRewardsAt && !args.ignoreAlreadyClaimed) {
    return 'complete-already-claimed';
  }

  // Passed all checks, rewards are available.
  return 'complete-rewards-available';
}

/**
 * Returns the remaining time for the current team chest event.
 */
export function getTeamChestEventRemainingTime(now: number): number {
  return (getCurrentTeamEventEndDate(now) ?? 0) - now;
}

/**
 * Calculate the target score for the current team chest event,
 * based on the previous event score and the number of members.
 */
export function calculateTeamChestTargetScore(
  teamState: TeamState,
  previousEventId?: string,
): number {
  const lastEventScore = getTeamEventScore(teamState, previousEventId);
  let score = teamChestTargetScore.min;

  if (lastEventScore > 0) {
    // Get the score from the previous event if it was set.
    score = lastEventScore;
  } else {
    // Otherwise, use the number of members to calculate the target score.
    const numMembers = Object.keys(teamState.global.members).length;
    score = numMembers * teamChestTargetScore.pointsPerPlayer;
  }

  const multipleOf = teamChestTargetScore.multipleOf;
  const targetScore = Math.ceil(score / multipleOf) * multipleOf;
  return clamp(targetScore, teamChestTargetScore.min, teamChestTargetScore.max);
}

/**
 * Get the target score for the current team chest event.
 */
export function getTeamChestEventTargetScore(
  teamState: TeamState,
  eventId: string,
  now: number,
): number {
  const event = teamState.global.events[eventId];
  if (event?.targetScore) {
    return event?.targetScore;
  }
  // No target score set yet, calculate it based on the previous event.
  const previousEventId = getPreviousTeamEventId(teamState, now);
  return calculateTeamChestTargetScore(teamState, previousEventId);
}

/**
 * Returns a list of randomised rewards for the team chest.
 */
export function getTeamChestRewards(seed: string): Match2Reward[] {
  const prng = createPrng(seed);

  return [
    // Grant 100-300 gems.
    {
      type: 'gems',
      amount: getRandomInt(
        teamChestRewards.gems.min,
        teamChestRewards.gems.max,
        prng,
      ),
    },
    // Grant 1-3 of a random booster.
    {
      type: 'boosters',
      subType: getRandomItemFromArray(
        [...teamChestRewards.boosters.types],
        prng,
      ),
      amount: getRandomInt(
        teamChestRewards.boosters.min,
        teamChestRewards.boosters.max,
        prng,
      ),
    },
    // Grant 1-3 of a random booster.
    {
      type: 'powerups',
      subType: getRandomItemFromArray(
        [...teamChestRewards.powerUps.types],
        prng,
      ),
      amount: getRandomInt(
        teamChestRewards.powerUps.min,
        teamChestRewards.powerUps.max,
        prng,
      ),
    },
  ];
}
